// Mirror of packages/shared/src/markdown.ts. Kept inline so the SDK
// stays workspace-dep-free. See the shared file for design notes.

export type InlineNode =
  | { type: 'text'; value: string }
  | { type: 'bold'; value: string }
  | { type: 'italic'; value: string }
  | { type: 'code'; value: string }
  | { type: 'link'; url: string; value: string };

const INLINE_MD =
  /(\*\*([\s\S]+?)\*\*)|(\*([^*\n]+?)\*)|(`([^`\n]+?)`)|(\[([^\]\n]+?)\]\(([^)\n]+?)\))/g;

export function parseInlineMarkdown(input: string): InlineNode[] {
  if (!input) return [];
  const out: InlineNode[] = [];
  let cursor = 0;
  INLINE_MD.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = INLINE_MD.exec(input)) !== null) {
    if (match.index > cursor) {
      out.push({ type: 'text', value: input.slice(cursor, match.index) });
    }
    if (match[1]) {
      out.push({ type: 'bold', value: match[2]! });
    } else if (match[3]) {
      out.push({ type: 'italic', value: match[4]! });
    } else if (match[5]) {
      out.push({ type: 'code', value: match[6]! });
    } else if (match[7]) {
      const url = match[9]!.trim();
      const text = match[8]!;
      if (isSafeLinkUrl(url)) {
        out.push({ type: 'link', url, value: text });
      } else {
        out.push({ type: 'text', value: match[7] });
      }
    }
    cursor = match.index + match[0].length;
  }
  if (cursor < input.length) {
    out.push({ type: 'text', value: input.slice(cursor) });
  }
  return out;
}

export function isSafeLinkUrl(url: string): boolean {
  const v = url.trim();
  if (v.length === 0) return false;
  if (v.startsWith('/') || v.startsWith('#') || v.startsWith('?')) return true;
  return /^(https?|mailto|tel):/i.test(v);
}

// Append parsed inline nodes onto a parent element using DOM APIs only
// (textContent for leaves, never innerHTML). Safe for untrusted body
// copy. Used by the tour runtime, checklist widget, and announcement
// renderer so authors can use **bold** / *italic* / `code` / [link](url)
// in any body field.
export function renderInlineMarkdownInto(
  parent: HTMLElement,
  input: string,
): void {
  parent.replaceChildren();
  const nodes = parseInlineMarkdown(input);
  for (const node of nodes) {
    if (node.type === 'text') {
      parent.appendChild(document.createTextNode(node.value));
    } else if (node.type === 'bold') {
      const el = document.createElement('strong');
      el.textContent = node.value;
      parent.appendChild(el);
    } else if (node.type === 'italic') {
      const el = document.createElement('em');
      el.textContent = node.value;
      parent.appendChild(el);
    } else if (node.type === 'code') {
      const el = document.createElement('code');
      el.textContent = node.value;
      parent.appendChild(el);
    } else if (node.type === 'link') {
      const el = document.createElement('a');
      el.textContent = node.value;
      el.href = node.url;
      // External links open in a new tab and don't leak the opener
      // window. Same-origin paths get the same treatment for
      // consistency - customers can override on their site if
      // they need same-tab nav.
      el.target = '_blank';
      el.rel = 'noopener noreferrer';
      parent.appendChild(el);
    }
  }
}
